import { KEYPAD_ISSUE, blocking, advisory } from '../catalog/issue';
import { KEY_STATES } from '../catalog/keyState';
import type { KeyState } from '../catalog/keyState';
import { offValueFor } from '../catalog/value';
import { findTarget, valueFitsTarget } from '../targets';
import type { MappingTarget } from '../targets';
import type { KeypadId, KeypadKeyId } from '../ids';
import { loadsFor, withKey, withStateLoads } from '../model';
import type { Keypad, KeypadBook, KeypadKey, LoadState } from '../model';
import { fail, ok } from '../result';
import type { KeypadResult } from '../result';

/**
 * What a key does when it is pressed, and when it is released.
 *
 * A state holds a list of outputs and the value each gets. A value's meaning comes
 * from the output it is stored against, so every command here checks values
 * against the target list the caller passes in: the feature keeps no copy of the
 * configuration, and a mapping can never be stored against an output the console
 * does not offer.
 */

/** The panel a command is about, or the issue that says it is gone. */
const keypadOr = (book: KeypadBook, keypadId: KeypadId): KeypadResult<Keypad> => {
  const keypad = book.keypads.find((entry) => entry.id === keypadId);
  if (keypad === undefined) {
    return fail(blocking(KEYPAD_ISSUE.KEYPAD_NOT_FOUND, 'That panel is no longer in the book.'));
  }
  return ok(keypad);
};

const keyOr = (keypad: Keypad, keyId: KeypadKeyId): KeypadResult<KeypadKey> => {
  const key = keypad.keys.find((entry) => entry.id === keyId);
  if (key === undefined) {
    return fail(blocking(KEYPAD_ISSUE.KEY_NOT_FOUND, 'That button is not on this panel.'));
  }
  return ok(key);
};

/** Everything one key state drives today. */
export const loadsOf = (
  book: KeypadBook,
  keypadId: KeypadId,
  keyId: KeypadKeyId,
  state: KeyState,
): KeypadResult<readonly LoadState[]> => {
  const keypad = keypadOr(book, keypadId);
  if (!keypad.ok) return keypad;
  const key = keyOr(keypad.value, keyId);
  if (!key.ok) return key;
  return ok(loadsFor(key.value, state));
};

/** The reason a list of (output, value) pairs cannot be stored, if any. */
const draftProblem = (
  targets: readonly MappingTarget[],
  loads: readonly LoadState[],
): string | null => {
  for (const load of loads) {
    const target = findTarget(targets, load.targetId);
    if (target === undefined) {
      return `“${load.targetId}” is not an output this console offers.`;
    }
    if (!valueFitsTarget(target, load.value)) {
      return `“${target.label}” cannot take the value ${load.value}.`;
    }
  }
  return null;
};

/** Stores a whole list for one state, refusing anything the outputs cannot do. */
const storeLoads = (
  book: KeypadBook,
  targets: readonly MappingTarget[],
  keypadId: KeypadId,
  keyId: KeypadKeyId,
  state: KeyState,
  loads: readonly LoadState[],
): KeypadResult<Keypad> => {
  const problem = draftProblem(targets, loads);
  if (problem !== null) return fail(blocking(KEYPAD_ISSUE.TARGET_MISSING, problem));

  const keypad = keypadOr(book, keypadId);
  if (!keypad.ok) return keypad;
  const key = keyOr(keypad.value, keyId);
  if (!key.ok) return key;

  return ok(withKey(keypad.value, keyId, (entry) => withStateLoads(entry, state, dedupe(loads))));
};

/** One value per output: the last one the technician set wins. */
const dedupe = (loads: readonly LoadState[]): readonly LoadState[] => {
  const byTarget = new Map<string, number>();
  for (const load of loads) byTarget.set(load.targetId, load.value);
  return [...byTarget].map(([targetId, value]) => ({ targetId, value }));
};

/**
 * Replaces everything one state drives.
 *
 * An empty list is allowed and means "this state does nothing": the state stays
 * on the key, so it can still be compared with what the device holds.
 */
export const setStateLoads = (
  book: KeypadBook,
  targets: readonly MappingTarget[],
  keypadId: KeypadId,
  keyId: KeypadKeyId,
  state: KeyState,
  loads: readonly LoadState[],
): KeypadResult<Keypad> => storeLoads(book, targets, keypadId, keyId, state, loads);

/** Adds an output to a state, or moves it when it is already there. */
export const addLoadToState = (
  book: KeypadBook,
  targets: readonly MappingTarget[],
  keypadId: KeypadId,
  keyId: KeypadKeyId,
  state: KeyState,
  load: LoadState,
): KeypadResult<Keypad> => {
  const current = loadsOf(book, keypadId, keyId, state);
  if (!current.ok) return current;
  return storeLoads(book, targets, keypadId, keyId, state, [
    ...current.value.filter((entry) => entry.targetId !== load.targetId),
    load,
  ]);
};

/** Drops an output from a state. */
export const removeLoadFromState = (
  book: KeypadBook,
  targets: readonly MappingTarget[],
  keypadId: KeypadId,
  keyId: KeypadKeyId,
  state: KeyState,
  targetId: string,
): KeypadResult<Keypad> => {
  const current = loadsOf(book, keypadId, keyId, state);
  if (!current.ok) return current;
  return storeLoads(
    book,
    targets,
    keypadId,
    keyId,
    state,
    current.value.filter((entry) => entry.targetId !== targetId),
  );
};

/** Changes the value one output is given in a state. */
export const setLoadValue = (
  book: KeypadBook,
  targets: readonly MappingTarget[],
  keypadId: KeypadId,
  keyId: KeypadKeyId,
  state: KeyState,
  load: LoadState,
): KeypadResult<Keypad> => addLoadToState(book, targets, keypadId, keyId, state, load);

/** Clears a state: the key does nothing on that half of the press. */
export const clearState = (
  book: KeypadBook,
  targets: readonly MappingTarget[],
  keypadId: KeypadId,
  keyId: KeypadKeyId,
  state: KeyState,
): KeypadResult<Keypad> => storeLoads(book, targets, keypadId, keyId, state, []);

/**
 * Copies one state onto the other, as the "off" value of each output.
 *
 * Turning a light on as the key goes down and off as it comes back up is what
 * most panels in a room are for, so the console offers it as one action. An
 * output the configuration no longer offers stops the copy rather than being
 * guessed at.
 */
export const mirrorAsOff = (
  book: KeypadBook,
  targets: readonly MappingTarget[],
  keypadId: KeypadId,
  keyId: KeypadKeyId,
  from: KeyState,
): KeypadResult<Keypad> => {
  const current = loadsOf(book, keypadId, keyId, from);
  if (!current.ok) return current;

  const to = KEY_STATES.find((state) => state !== from);
  if (to === undefined) {
    return fail(advisory(KEYPAD_ISSUE.UNKNOWN_STATE, 'That key has no second state to copy onto.'));
  }

  const mirrored: LoadState[] = [];
  for (const load of current.value) {
    const target = findTarget(targets, load.targetId);
    if (target === undefined) {
      return fail(
        blocking(
          KEYPAD_ISSUE.TARGET_MISSING,
          'One of the outputs in this state is no longer in the configuration, so it cannot be copied.',
        ),
      );
    }
    mirrored.push({ targetId: target.id, value: offValueFor(target.valueKind) });
  }

  return storeLoads(book, targets, keypadId, keyId, to, mirrored);
};
